import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ActionCard from 'components/Common/Cards/ActionCard';
import Button, { ButtonType } from 'components/Button';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getApplicationData } from 'selectors/getApplicationData';
import { DebtSummary, HardOfferData } from 'handlers/applicationData';
import { normalizeDebtSummary } from 'components/FinancialCheckup/YourFinances/yourFinancesUtils';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { AICoachGoalType } from 'enums/AICoach';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useOpenAiCoachConversationChat from 'hooks/useOpenAiCoachConversationChat';
import { getGoalProgress, getNextSteps, startScoreImprovementPlan } from 'thunks';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import { hasPrimaryGoal } from 'utils/aiCoachUtils';

import { ReactComponent as UpwardTrendIcon } from 'images/upward-trend-icon.svg';
import { ReactComponent as DownwardTrendIcon } from 'images/downward-trend-icon.svg';
import { ReactComponent as WalletIcon } from 'images/repayment-items/wallet-icon.svg';
import { ReactComponent as SparklesIcon } from 'images/sparkles-icon.svg';
import { ReactComponent as EquifaxLogo } from 'images/equifax-logo.svg';

import {
  getConsolidatableMaxRemainingMonths,
  getSelectedAICoachGoal,
} from 'components/FinancialCheckup/FinancialDashboard/YourGoalSection/getYourGoalTrajectory';

import TrajectoryComparison from './TrajectoryComparison';
import {
  AcceleratePrimaryAction,
  resolveAccelerateYourGoalsCard,
  resolveTrajectoryComparison,
} from './resolveAccelerateYourGoalsCard';

import styles from './AccelerateYourGoalsSection.module.scss';

const GOAL_ICONS: Record<AICoachGoalType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [AICoachGoalType.PayDownCreditCardDebt]: DownwardTrendIcon,
  [AICoachGoalType.ImproveCreditScore]: DownwardTrendIcon,
  [AICoachGoalType.BuildEmergencyFund]: UpwardTrendIcon,
  [AICoachGoalType.PayDownStudentLoanDebt]: UpwardTrendIcon,
  [AICoachGoalType.SaveForSpecificExpense]: UpwardTrendIcon,
  [AICoachGoalType.Other]: UpwardTrendIcon,
  [AICoachGoalType.SaveForRetirement]: UpwardTrendIcon,
};

const AccelerateYourGoalsSection: React.FC = () => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const openAiCoachConversationChat = useOpenAiCoachConversationChat();

  const { aiCoachGoals, isStartingScoreImprovementPlan } = useSelector(getLoanGoal);
  const { application } = useSelector(getApplicationData);
  const selectedGoal = getSelectedAICoachGoal(aiCoachGoals);

  if (
    !application ||
    !selectedGoal ||
    ![ApplicationStatusName.OfferAvailable, ApplicationStatusName.Rejected].includes(application.status)
  )
    return null;

  const isRejected = application.status === ApplicationStatusName.Rejected;
  const isCreditScoreWithOffer = selectedGoal.type === AICoachGoalType.ImproveCreditScore && !isRejected;

  if (hasPrimaryGoal(aiCoachGoals) && !isCreditScoreWithOffer) return null;

  const selectedAccelerateGoal = isCreditScoreWithOffer
    ? { ...selectedGoal, type: AICoachGoalType.PayDownCreditCardDebt }
    : selectedGoal;
  const hardOffer = (application.hardOffer ?? {}) as Partial<HardOfferData>;
  const rawDebtSummary = application.debtSummary as DebtSummary | undefined;
  const debtSummary = rawDebtSummary ? normalizeDebtSummary(rawDebtSummary) : undefined;
  const cardContent = resolveAccelerateYourGoalsCard(
    selectedGoal,
    isRejected,
    hardOffer,
    application.optimalPathPlan?.scoreImprovement,
    debtSummary,
  );

  if (!cardContent) return null;

  const currentMonths = getConsolidatableMaxRemainingMonths(debtSummary);
  const planneryMonths = hardOffer.planneryLoan?.term ?? application.loanTermInMonths;
  const trajectory = resolveTrajectoryComparison(
    selectedAccelerateGoal,
    hardOffer,
    currentMonths,
    planneryMonths,
    debtSummary,
  );

  const GoalIcon = GOAL_ICONS[selectedAccelerateGoal.type] ?? WalletIcon;

  const handlePrimaryClick = async () => {
    if (cardContent.primaryAction === AcceleratePrimaryAction.EnrollScoreIQ) {
      try {
        await dispatchWithUnwrap(startScoreImprovementPlan(application.id));
        if (application.borrowerId) {
          dispatch(getGoalProgress(application.borrowerId));
          dispatch(getNextSteps(application.borrowerId));
        }
      } catch (error) {
        analytics.track('Failed to start score improvement plan', { error });
      }
      return;
    }

    navigate(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
  };

  return (
    <div className={styles.nextStepSection}>
      <p className={styles.sectionLabel}>Accelerate Your Goals</p>
      <ActionCard>
        <ActionCard.Row className={styles.titleRow}>
          <div className={styles.iconCircle}>
            <GoalIcon />
          </div>
          <div className={styles.titleBlock}>
            <ActionCard.Title className={styles.title}>{cardContent.title}</ActionCard.Title>
            {cardContent.showScoreIQAttribution && (
              <div className={styles.poweredBy}>
                <span className={styles.poweredByText}>Powered by</span>
                <EquifaxLogo className={styles.equifaxLogo} />
              </div>
            )}
          </div>
        </ActionCard.Row>
        {cardContent.subtitle && <ActionCard.Description>{cardContent.subtitle}</ActionCard.Description>}
        {cardContent.bullets && cardContent.bullets.length > 0 && (
          <div className={styles.bulletsContainer}>
            <ol className={styles.bullets}>
              {cardContent.bullets.map((bullet) => (
                <li key={bullet} className={styles.bullet}>
                  {bullet}
                </li>
              ))}
            </ol>
          </div>
        )}
        {trajectory && (
          <TrajectoryComparison currentValue={trajectory.currentValue} planneryValue={trajectory.planneryValue} />
        )}
        <Button type={ButtonType.Inverted} onClick={handlePrimaryClick} isLoading={isStartingScoreImprovementPlan}>
          {cardContent.primaryButtonText}
        </Button>
        <Button type={ButtonType.Transparent} className={styles.explainButton} onClick={openAiCoachConversationChat}>
          <SparklesIcon className={styles.sparklesIcon} />
          EXPLAIN THIS GUIDANCE
        </Button>
      </ActionCard>
    </div>
  );
};

export default AccelerateYourGoalsSection;
